<script lang="ts">
	import { useDocuments } from "../hooks/useDocuments";
	import  * as Card from "$lib/components/ui/card";
	import { ScrollArea } from "$lib/components/ui/scroll-area/index.js";
	import LoadingIcon from "$src/presentation/defaults/loading-icon.svelte";
	import { t  } from 'svelte-i18n'
	import TimeSince from "$src/presentation/core/time-since.svelte";
	import DocumentIcon from "./document-icon.svelte";
	import type { DocumentSummary } from "$src/domain/projects/documents/DocumentSummary";
  	import { createEventDispatcher, onDestroy, onMount } from "svelte";
    import NewDocument from "../edit/new-document.svelte";
    import DocumentUploadStatus from "../edit/document-upload-status.svelte";
    import { documentRefresh, documentUploads } from '$src/infrastructure/projects/documents/uploadDocumentStore';
	import * as Alert from "$lib/components/ui/alert/index.js";
    import { Input } from "$lib/components/ui/input";
	import { Badge } from "$lib/components/ui/badge/index.js";
    import { Button } from "$lib/components/ui/button";
    import { DocType } from "$src/domain/projects/documents/DoctType";

	export let groupStoreId: number
	export let projectId: number
	export let selectedDocumentId: string | undefined

	let filterText = '';

    $: queryResult = useDocuments(groupStoreId, projectId)

	const dispatch = createEventDispatcher();
	function selectDocument(document: DocumentSummary) {
		selectedDocumentId = document.id;
		dispatch('documentClick', document.id);
	}

	onMount(() => {
		window.addEventListener('keydown', handleKeyPress);
		document.addEventListener('nextDocument', selectNextDocument);
		document.addEventListener('previousDocument', selectPreviousDocument);
	});

	onDestroy(() => {
		window.removeEventListener('keydown', handleKeyPress);
		document.removeEventListener('nextDocument', selectNextDocument);
		document.removeEventListener('previousDocument', selectPreviousDocument);
	});

	const handleKeyPress = (event: KeyboardEvent) => {
		switch (event.key) {
			case 'ArrowLeft':
				selectPreviousDocument();
				break;
			case 'ArrowRight':
				selectNextDocument();
				break;
			case 'ArrowUp':
				selectPreviousDocument();
				break;
			case 'ArrowDown':
				selectNextDocument();
				break;
		}
	};


	function selectNextDocument() {
		if( $queryResult.data )
		{
			if( selectedDocumentId ) {
				var currentIndex = $queryResult.data?.findIndex(document => document.id == selectedDocumentId);
				if (currentIndex !== -1) {
					if (currentIndex + 1 < $queryResult.data.length) {
						const nextDocument = $queryResult.data[currentIndex + 1];
						selectDocument(nextDocument);
					} else {
						const firstDocument = $queryResult.data[0];
						selectDocument(firstDocument);
					}
				}
			}	
		}
	}

	function selectPreviousDocument() {
		if( $queryResult.data )
		{
			if( selectedDocumentId ) {
				var currentIndex = $queryResult.data?.findIndex(document => document.id == selectedDocumentId);
				if (currentIndex !== -1) {
					if (currentIndex - 1 >= 0) {
						const previousDocument = $queryResult.data[currentIndex - 1];
						selectDocument(previousDocument);
					} else {
						const lastDocument = $queryResult.data[$queryResult.data.length - 1];
						selectDocument(lastDocument);
					}
				}
			}	
		}
	}

	function refreshData() {
		$queryResult.refetch();
	}

	$: if( $documentRefresh ) {
		refreshData();
	}

	$: filteredDocuments = $queryResult.data
		? $queryResult.data.filter(document => 
			document.title.toLowerCase().includes(filterText.toLowerCase()) ||
			(document.folderName && document.folderName.toLowerCase().includes(filterText.toLowerCase())) ||
			(document.documentType == DocType.PDF && filterText.includes('*.pdf'))
		)
		: [];
</script>


<Card.Root class="flex flex-col w-full flew-grow overflow-hidden">
	<Card.Header >
		<Card.Title class="flex flew-row w-full justify-between">
			<div class="flex-grow">{$t('page.projects.documents.list-title')}</div>
			<NewDocument groupStoreId={groupStoreId} projectId={projectId} />
			{#if $documentUploads.length > 0}
				<DocumentUploadStatus />
			{/if}
		</Card.Title>
		<div class="flex items-center pt-2 gap-2">
			<Input
			  class="max-w-sm"
			  placeholder={$t('page.projects.documents.filter') + '...'}
			  type="text"
			  bind:value={filterText}
			/>
			{#if filterText}
				<Button
					size="sm"
					on:click={() => filterText = ''}
					aria-label="Clear filter"
				>
					&times;
				</Button>
			{/if}
		</div>
	</Card.Header>
	{#if $queryResult.status === 'loading'}
			<LoadingIcon />
	{:else if $queryResult.isSuccess}
	<Card.Content  class="flex flew-grow overflow-hidden">
			{#if filteredDocuments.length > 0}
			<ScrollArea orientation="vertical" class="h-full w-full">
					<div class="flex flex-col gap-2 pt-0">
						{#each filteredDocuments as document}
							<button
								class="flex flex-col gap-2 rounded-lg border p-3 text-left text-sm transition-all hover:bg-accent {selectedDocumentId === document.id && "bg-muted"}"
								on:click={() => selectDocument(document)}
							>
								<div class="flex w-full gap-1">
									<div class="flex flex-grow items-center gap-2 overflow-hidden">
										<DocumentIcon document={document} />
										<div class="font-semibold truncate">
											{document.title}
										</div>
									</div>
									<div class="w-32 text-right text-xs">
										<TimeSince dateString={document.dateCreated} />
									</div>
								</div>
								<div class="flex w-full justify-end text-xs  text-muted-foreground uppercase">
									{#if document.folderName}
										<Badge variant="secondary" >
											{document.folderName}
										</Badge>
									{/if}
								</div>
							</button>
						{/each}
					</div>
				</ScrollArea> 
			{:else}
				<div class="flex flex-col h-full w-full items-center justify-center">
					<div class="h-1/3">
						{$t('global.messages.noresult')}
					</div>
					<Alert.Root class="max-w-sm">
						<Alert.Title> {$t('global.messages.tip')}</Alert.Title>
						<Alert.Description>
							{#if $queryResult.data.length == 0}
								{$t('global.messages.app-tips.add-new-items')}
							{:else}
								<div class="flex gap-2 justify-center items-center">
									{$t('global.messages.app-tips.clear-filter')}
									<Button
										size="sm"
										on:click={() => filterText = ''}
										aria-label="Clear filter"
									>
										&times;
									</Button>
								</div>
								
							{/if}
						</Alert.Description>
					</Alert.Root>
				</div>
			{/if}
		</Card.Content>
		<Card.Footer class="flex justify-end items-end">
			<div class="flex flex-row justify-end  items-center gap-1 text-sm italic">
				{$queryResult.data.length} {$t('page.projects.documents.documents')}
			</div>
		</Card.Footer>
	{:else if $queryResult.status === 'error'}
		<p style="color: red">{$queryResult.error.message}</p>
	{/if}
</Card.Root> 

